import { mcpIntegrationCreateSchema, mcpIntegrationUpdateSchema } from "@yazamco/validation";
import { useEffect, useState } from "react";
import type {
  McpAdminAuthConfig,
  McpAdminIntegrationListItem,
  McpDiscoveredToolView,
  McpDiscoveryResult,
  McpIntegrationTestResult,
  McpIntegrationSummary,
  McpOauthDiscoveryView,
} from "@yazamco/shared-types";
import { useToast } from "../../../components/feedback/Toast";
import { useConfirm } from "../../../components/feedback/ConfirmDialog";
import { Icon } from "../../../components/ui/Icon";
import {
  useCreateMcpIntegration,
  useDeleteMcpIntegration,
  useDiscoverMcpOauth,
  useDiscoverMcpTools,
  useMcpAdminIntegrations,
  useMcpOauthCallbackUrl,
  useSetMcpAllowedTools,
  useTestMcpIntegration,
  useUpdateMcpIntegration,
} from "../mcp-admin-hooks";
import { BackendUnavailable } from "./BackendUnavailable";
import {
  useConnectMcpIntegration,
  useMcpIntegrations,
} from "../../copilot/hooks";
import { mcpOauthStartUrl } from "../../../lib/copilot";

/**
 * Settings → Business Integrations: the GLOBAL MCP catalog, administered.
 *
 * This screen exists so that adding Monday stops being a deployment. Before it,
 * a new business system meant editing `COPILOT_MCP_SERVERS`, editing
 * docker-compose, and restarting the API; the same act is now a wizard, and the
 * change is live for every user on the next request.
 *
 * ── Three decisions worth reading ───────────────────────────────────────────
 *
 * 1. IT IS NOT IN THE COPILOT TAB. Deliberately. The per-question Business
 *    tools picker is where a USER connects their own account and chooses their
 *    own tools; this is where somebody decides what business systems exist at
 *    all. Burying global administration inside the picker would put the two one
 *    click apart and make the second look like a power-user version of the
 *    first, which is exactly the confusion the separate permission exists to
 *    prevent.
 *
 * 2. SECRETS ARE WRITE-ONLY, and the UI is built around not having them. There
 *    is no state in this component that holds a stored credential, because the
 *    API cannot send one: `secrets.hasClientSecret` is a boolean, and the edit
 *    form renders "Configured ✓ / Replace" from it. Pressing Replace reveals an
 *    empty field; leaving it alone OMITS the field from the request, which the
 *    API reads as "keep what you have". A password input pre-filled with dots
 *    would be a lie about what the browser knows.
 *
 * 3. THE TOOL ALLOWLIST IS BUILT FROM LIVE DISCOVERY, never typed. An
 *    administrator presses Discover, sees what the server actually offers with
 *    each tool marked safe or not, and ticks the safe ones. A destructive tool
 *    is SHOWN and DISABLED rather than hidden — hiding it would leave somebody
 *    wondering whether discovery had worked — and the checkbox being disabled
 *    is a courtesy: the API refuses it regardless of what the browser sends.
 *
 * The whole screen is hidden from a caller without `copilot.mcp_integrations_read`,
 * and every control within it from one without `..._manage`. That is an
 * affordance and not the boundary; the API is the boundary, and it is asked
 * again on every request.
 */

type AuthKind = "none" | "oauth2-user" | "token-user" | "service-account";

/** The wizard's editable state. Flat, because a form is flat. */
interface FormState {
  protocol: "mcp" | "text-webhook";
  slug: string;
  displayName: string;
  description: string;
  icon: string;
  endpoint: string;
  timeoutMs: number;
  authKind: AuthKind;
  // token-user
  credentialLabel: string;
  credentialHint: string;
  // oauth2-user
  authorizationUrl: string;
  tokenUrl: string;
  revocationUrl: string;
  clientId: string;
  /**
   * Where the provider will register an OAuth app for this deployment, when it
   * publishes one. Filled in by Detect settings and never typed.
   *
   * Its presence is what makes `clientId` optional: with it, saving the
   * integration has the provider ISSUE an id instead of asking for one.
   */
  registrationEndpoint: string;
  scopesText: string;
  pkce: boolean;
  audience: string;
  accountNameField: string;
  // transport, shared by every kind that has one
  transportKind: "bearer" | "header";
  transportHeader: string;
  /**
   * The secret, as typed. Three states, mirroring the API's:
   *   null   → not being changed (the stored one stays)
   *   ""     → being cleared
   *   "…"    → being replaced
   * `null` is the default on an edit and is what makes "save the name without
   * retyping the client secret" a single request.
   */
  secret: string | null;
}

const EMPTY_FORM: FormState = {
  protocol: "mcp",
  slug: "",
  displayName: "",
  description: "",
  icon: "",
  endpoint: "",
  timeoutMs: 15_000,
  authKind: "oauth2-user",
  credentialLabel: "API token",
  credentialHint: "",
  authorizationUrl: "",
  tokenUrl: "",
  revocationUrl: "",
  clientId: "",
  registrationEndpoint: "",
  scopesText: "",
  pkce: true,
  audience: "",
  accountNameField: "",
  transportKind: "bearer",
  transportHeader: "",
  // On CREATE the field starts empty-and-being-set rather than "unchanged":
  // there is nothing stored to keep.
  secret: "",
};

const AUTH_LABELS: Record<AuthKind, string> = {
  "oauth2-user": "OAuth 2.0 — each user connects their own account",
  "token-user": "Personal API token — each user enters their own token",
  none: "No authentication",
  "service-account": "Shared service account",
};

/** The short form, for the list card. */
const AUTH_SHORT: Record<AuthKind, string> = {
  "oauth2-user": "OAuth 2.0",
  "token-user": "Personal API token",
  none: "No authentication",
  "service-account": "Shared service account",
};

const FIELD_LABELS: Record<string, string> = {
  slug: "ID / slug",
  displayName: "Name",
  endpoint: "MCP endpoint",
  icon: "MCP logo",
  timeoutMs: "Timeout",
  "auth.authorizationUrl": "Authorization URL",
  "auth.tokenUrl": "Token URL",
  "auth.clientId": "Client ID",
  "auth.registrationEndpoint": "OAuth registration endpoint",
  "auth.transport.header": "Credential header",
};

function formFrom(item: McpAdminIntegrationListItem): FormState {
  const auth = item.auth;
  const transport = "transport" in auth ? auth.transport : { kind: "bearer" as const };
  return {
    ...EMPTY_FORM,
    protocol: item.protocol ?? "mcp",
    slug: item.slug,
    displayName: item.displayName,
    description: item.description ?? "",
    icon: item.icon ?? "",
    endpoint: item.endpoint,
    timeoutMs: item.timeoutMs,
    authKind: auth.kind,
    credentialLabel: auth.kind === "token-user" ? auth.credentialLabel : "API token",
    credentialHint: auth.kind === "token-user" ? (auth.credentialHint ?? "") : "",
    authorizationUrl: auth.kind === "oauth2-user" ? auth.authorizationUrl : "",
    tokenUrl: auth.kind === "oauth2-user" ? auth.tokenUrl : "",
    revocationUrl: auth.kind === "oauth2-user" ? (auth.revocationUrl ?? "") : "",
    clientId: auth.kind === "oauth2-user" ? auth.clientId : "",
    registrationEndpoint:
      auth.kind === "oauth2-user" ? (auth.registrationEndpoint ?? "") : "",
    scopesText: auth.kind === "oauth2-user" ? auth.scopes.join("\n") : "",
    pkce: auth.kind === "oauth2-user" ? auth.pkce : true,
    audience: auth.kind === "oauth2-user" ? (auth.audience ?? "") : "",
    accountNameField: auth.kind === "oauth2-user" ? (auth.accountNameField ?? "") : "",
    transportKind: transport.kind,
    transportHeader: transport.kind === "header" ? transport.header : "",
    // The stored secret is UNCHANGED until somebody presses Replace. This is
    // the whole write-only story in one initial value.
    secret: null,
  };
}

/** The host of a URL, for a sentence naming who is about to be asked for something. */
function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return "the provider";
  }
}

function linesToArray(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

/** A stable default id so the ordinary create flow never asks for one. */
function suggestedSlug(name: string, endpoint: string): string {
  const slugify = (value: string): string =>
    value
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 64);
  const fromName = slugify(name);
  if (fromName) return fromName;
  try {
    const [hostLabel = ""] = new URL(endpoint).hostname.split(".");
    return slugify(hostLabel) || "business-tool";
  } catch {
    return "business-tool";
  }
}

/**
 * Build the `auth` block a request carries.
 *
 * The secret field is included ONLY when it is being set or cleared. That is
 * the three-state contract, and it is the reason an edit that renames an
 * integration cannot blank its client secret by omission.
 */
function authPayload(form: FormState): Record<string, unknown> {
  const transport =
    form.transportKind === "header"
      ? { kind: "header", header: form.transportHeader.trim() }
      : { kind: "bearer" };

  switch (form.authKind) {
    case "none":
      return { kind: "none" };
    case "service-account":
      return {
        kind: "service-account",
        shared: true,
        transport,
        ...(form.secret === null
          ? {}
          : { serviceAccountCredential: form.secret === "" ? null : form.secret }),
      };
    case "token-user":
      return {
        kind: "token-user",
        transport,
        credentialLabel: form.credentialLabel.trim() || "API token",
        credentialHint: form.credentialHint.trim() || null,
      };
    case "oauth2-user":
      return {
        kind: "oauth2-user",
        authorizationUrl: form.authorizationUrl.trim(),
        tokenUrl: form.tokenUrl.trim(),
        revocationUrl: form.revocationUrl.trim() || null,
        // OMITTED rather than sent empty when nothing was typed. An empty
        // string is a client id the schema refuses; an absent field is the
        // request that says "register one", and the two must not be confused by
        // a `.trim()` that turns the second into the first.
        ...(form.clientId.trim() ? { clientId: form.clientId.trim() } : {}),
        registrationEndpoint: form.registrationEndpoint.trim() || null,
        scopes: linesToArray(form.scopesText),
        pkce: form.pkce,
        audience: form.audience.trim() || null,
        accountNameField: form.accountNameField.trim() || null,
        transport,
        ...(form.secret === null
          ? {}
          : { clientSecret: form.secret === "" ? null : form.secret }),
      };
  }
}

export function BusinessIntegrationsTab({
  canManage,
}: {
  canManage: boolean;
}): JSX.Element {
  const toast = useToast();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const confirm = useConfirm();
  const integrations = useMcpAdminIntegrations(true);
  const callback = useMcpOauthCallbackUrl(canManage);
  const create = useCreateMcpIntegration();
  const update = useUpdateMcpIntegration();
  const remove = useDeleteMcpIntegration();
  const test = useTestMcpIntegration();
  const discover = useDiscoverMcpTools();
  const detectOauth = useDiscoverMcpOauth();
  const setAllowed = useSetMcpAllowedTools();
  const personalIntegrations = useMcpIntegrations(canManage);

  // null = closed; { id: null } = adding; { id } = managing that integration.
  const [editing, setEditing] = useState<{ id: string | null } | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [discovery, setDiscovery] = useState<McpDiscoveryResult | null>(null);
  const [testResult, setTestResult] = useState<McpIntegrationTestResult | null>(null);
  /**
   * What the provider said about itself, when Detect settings has been pressed.
   *
   * Kept beside the form rather than folded into it, because the two answer
   * different questions. The form is what will be SAVED; this is what the
   * provider CLAIMED, and an administrator needs to see the second to judge the
   * first — above all the hosts, which is the check no rule can make for them.
   */
  const [detected, setDetected] = useState<McpOauthDiscoveryView | null>(null);

  const items = integrations.data?.items ?? [];
  const current =
    editing?.id != null ? (items.find((item) => item.id === editing.id) ?? null) : null;
  const personalIntegration = current
    ? (personalIntegrations.data?.items.find((item) => item.id === current.id) ?? null)
    : null;
  const saving = create.isPending || update.isPending;

  // Discovery belongs to ONE integration. Leaving a previous integration's tool
  // list on screen after switching would invite an administrator to approve
  // Hudu's tools for Monday.
  useEffect(() => {
    setDiscovery(null);
    setTestResult(null);
    setDetected(null);
  }, [editing?.id]);

  function openCreate(): void {
    setValidationErrors([]);
    setForm(EMPTY_FORM);
    setEditing({ id: null });
  }
  function openManage(item: McpAdminIntegrationListItem): void {
    setValidationErrors([]);
    setForm(formFrom(item));
    setEditing({ id: item.id });
  }
  function close(): void {
    setEditing(null);
  }

  // Escape closes the editor, alongside the backdrop click (§7). A keydown
  // handler on the overlay would only fire once something inside it had focus.
  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setEditing(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [editing]);

  /**
   * Ask the provider what its OAuth settings are, and fill the form in.
   *
   * Three things about what this does and does not do.
   *
   * It OVERWRITES the three URL fields rather than filling only the empty ones.
   * A half-detected form — one URL from the provider and two from a previous
   * paste — is the configuration hardest to debug and the one a partial fill
   * would quietly produce.
   *
   * It leaves the CLIENT ID alone. Nothing came back to put there: the id is
   * issued on save, and clearing a manually entered one would throw away
   * something the administrator may still want to keep.
   *
   * It does not narrow the SCOPES. `scopes_supported` is everything the server
   * will grant, and copying all of it into the request would ask every user for
   * the widest consent the provider offers. The list is shown so an
   * administrator can pick from it.
   */
  async function detect(base: FormState = form): Promise<FormState | null> {
    const endpoint = base.endpoint.trim();
    if (!endpoint) {
      toast.error("Enter the MCP endpoint first.");
      return null;
    }
    try {
      const { discovery: found } = await detectOauth.mutateAsync({
        endpoint,
        ...(base.displayName.trim() ? { clientName: base.displayName.trim() } : {}),
      });
      setDetected(found);
      const next = {
        ...base,
        authorizationUrl: found.authorizationUrl,
        tokenUrl: found.tokenUrl,
        revocationUrl: found.revocationUrl ?? "",
        registrationEndpoint: found.registrationEndpoint ?? "",
        pkce: found.pkce,
      };
      setForm(next);
      toast.success(
        found.registrationEndpoint
          ? "OAuth settings detected. No client ID needed — one is registered when you save."
          : "OAuth settings detected. Enter the client ID from the provider.",
      );
      return next;
    } catch (error) {
      setDetected(null);
      toast.error(
        error instanceof Error
          ? error.message
          : "The provider could not be asked for its OAuth settings.",
      );
      return null;
    }
  }

  async function save(): Promise<void> {
    let effectiveForm = form;
    // OAuth metadata is provider-owned configuration. The normal flow detects
    // it at save time; the advanced panel exists only for review/override.
    if (
      effectiveForm.protocol === "mcp" &&
      effectiveForm.authKind === "oauth2-user" &&
      (!effectiveForm.authorizationUrl.trim() || !effectiveForm.tokenUrl.trim())
    ) {
      const detectedForm = await detect(effectiveForm);
      if (!detectedForm) return;
      effectiveForm = detectedForm;
    }
    const slug =
      effectiveForm.slug.trim() ||
      suggestedSlug(effectiveForm.displayName, effectiveForm.endpoint);
    const candidate = {
      ...(editing?.id == null ? { protocol: effectiveForm.protocol } : {}),
      displayName: effectiveForm.displayName.trim(),
      description: effectiveForm.description.trim() || null,
      icon: effectiveForm.icon.trim() || null,
      endpoint: effectiveForm.endpoint.trim(),
      timeoutMs: effectiveForm.timeoutMs,
      auth: authPayload(effectiveForm),
      ...(editing?.id == null
        ? { slug }
        : { version: current?.version }),
    };
    // The server remains authoritative about localhost policy.
    const schema = editing?.id == null
      ? mcpIntegrationCreateSchema({ allowInsecureLocalhost: true })
      : mcpIntegrationUpdateSchema({ allowInsecureLocalhost: true });
    const parsed = schema.safeParse(candidate);
    if (!parsed.success) {
      setValidationErrors(
        parsed.error.issues.map(
          (issue) => {
            const path = issue.path.join(".");
            return `${FIELD_LABELS[path] ?? path}: ${issue.message}`;
          },
        ),
      );
      return;
    }
    setValidationErrors([]);
    try {
      if (editing?.id == null) {
        const { integration } = await create.mutateAsync({
          slug,
          protocol: effectiveForm.protocol,
          displayName: effectiveForm.displayName.trim(),
          description: effectiveForm.description.trim() || undefined,
          icon: effectiveForm.icon.trim() || undefined,
          endpoint: effectiveForm.endpoint.trim(),
          timeoutMs: effectiveForm.timeoutMs,
          readOnly: true,
          // Always created switched off. The next steps are Connect, Discover,
          // approve tools, Enable — and an integration published before any of
          // them would offer users a source that cannot answer.
          enabled: false,
          allowedTools: effectiveForm.protocol === "text-webhook" ? ["search"] : [],
          auth: authPayload(effectiveForm),
        } as never);
        toast.success(
          `${integration.displayName} created. Connect your own account, then discover its tools.`,
        );
        // Straight into the manage view: creating is step one of five, and
        // closing here would make an administrator find the row and reopen it.
        setEditing({ id: integration.id });
        setForm(formFrom(integration));
      } else if (current) {
        await update.mutateAsync({
          id: current.id,
          input: {
            version: current.version,
            displayName: effectiveForm.displayName.trim(),
            description: effectiveForm.description.trim() || null,
            icon: effectiveForm.icon.trim() || null,
            endpoint: effectiveForm.endpoint.trim(),
            timeoutMs: effectiveForm.timeoutMs,
            auth: authPayload(effectiveForm),
          } as never,
        });
        toast.success("Integration saved");
        // The secret returns to "unchanged" after a successful save, so a
        // second save does not resend a value the administrator has already
        // committed.
        setForm({ ...effectiveForm, secret: null });
      }
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function toggleEnabled(item: McpAdminIntegrationListItem): Promise<void> {
    try {
      await update.mutateAsync({
        id: item.id,
        input: { version: item.version, enabled: !item.enabled } as never,
      });
      toast.success(
        item.enabled
          ? `${item.displayName} disabled — nobody can use it from now on.`
          : `${item.displayName} enabled.`,
      );
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function runTest(item: McpAdminIntegrationListItem): Promise<void> {
    try {
      const { result } = await test.mutateAsync(item.id);
      setTestResult(result);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function runDiscovery(item: McpAdminIntegrationListItem): Promise<void> {
    try {
      setDiscovery(await discover.mutateAsync(item.id));
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function saveAllowed(
    item: McpAdminIntegrationListItem,
    tools: string[],
  ): Promise<void> {
    try {
      await setAllowed.mutateAsync({ id: item.id, tools, version: item.version });
      toast.success("Allowed tools saved");
      setDiscovery(
        discovery
          ? {
              ...discovery,
              items: discovery.items.map((tool) => ({
                ...tool,
                allowed: tools.includes(tool.name),
              })),
            }
          : null,
      );
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onDelete(item: McpAdminIntegrationListItem): Promise<void> {
    const { connectedUsers, storedToolPreferences } = item.impact;
    const ok = await confirm({
      title: `Delete ${item.displayName}?`,
      // The blast radius, before the decision. An administrator needs to know
      // that removing Monday disconnects twelve people; they do not need, and
      // are not given, the list of who those people are.
      body:
        `This removes the integration for everyone. ` +
        `${connectedUsers} user${connectedUsers === 1 ? "" : "s"} connected, ` +
        `${storedToolPreferences} stored tool preference${storedToolPreferences === 1 ? "" : "s"}. ` +
        `Their stored credentials are deleted and cannot be recovered.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await remove.mutateAsync({ id: item.id, confirmSlug: item.slug });
      toast.success(`${item.displayName} deleted`);
      if (editing?.id === item.id) close();
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  if (integrations.isError) {
    return (
      <section className="set-list set-list-empty">
        <p className="field-error" role="alert">
          Could not load business integrations:{" "}
          {(integrations.error as Error).message}
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void integrations.refetch()}
        >
          Retry
        </button>
      </section>
    );
  }

  return (
    <>
    <section className="set-section" data-testid="business-integrations">
      <div className="set-section-head">
        <div>
          <h2>MCP catalog</h2>
          <p>
            Business systems Copilot may call as tools. Each user still connects
            and authorizes their own account.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={openCreate}
            data-testid="mcp-add"
          >
            <Icon name="plus" size={14} stroke={2.1} />
            Add integration
          </button>
        )}
      </div>

      {integrations.isPending ? (
        <div className="set-list set-list-empty">Loading integrations…</div>
      ) : items.length === 0 ? (
        <div className="set-list set-list-empty" data-testid="mcp-empty">
          No business integrations yet. Add one to let Copilot answer from
          Monday, Hudu or another MCP server — no redeploy needed.
        </div>
      ) : (
        <div className="set-list">
          {items.map((item) => {
            const failing = item.configurationWarnings.length > 0;
            const status = failing ? "Failing" : item.enabled ? "Connected" : "Disabled";
            return (
              <div className="set-row" key={item.id} data-testid={`mcp-row-${item.slug}`}>
                <McpLogo icon={item.icon} name={item.displayName} />

                <div className="set-row-identity">
                  <strong>{item.displayName}</strong>
                  <span
                    className={`set-status set-status-${failing ? "danger" : item.enabled ? "ok" : "off"}`}
                    data-testid={`mcp-status-${item.slug}`}
                  >
                    <span aria-hidden />
                    {status}
                  </span>
                  <span className="set-auth">{AUTH_SHORT[item.auth.kind]}</span>
                </div>

                <div className="set-row-detail">
                  {failing ? (
                    <p className="set-row-warning" data-testid={`mcp-warning-${item.slug}`}>
                      {item.configurationWarnings[0]}
                    </p>
                  ) : (
                    item.endpoint && <p title={item.endpoint}>{item.endpoint}</p>
                  )}
                  <p className="mono" data-testid={`mcp-tools-${item.slug}`}>
                    slug: {item.slug} ·{" "}
                    {item.protocol === "text-webhook"
                      ? "1 exclusive tool"
                      : `${item.allowedTools.length} allowed tool${item.allowedTools.length === 1 ? "" : "s"}`}
                  </p>
                </div>

                {canManage && (
                  <div className="set-row-actions">
                    <button
                      type="button"
                      className="set-configure"
                      onClick={() => openManage(item)}
                      data-testid={`mcp-manage-${item.slug}`}
                    >
                      Configure
                    </button>
                    {/* The switch is the whole "is this available to anyone"
                        decision, so it sits on the row rather than behind the
                        Configure dialog. */}
                    <button
                      type="button"
                      role="switch"
                      className="switch"
                      aria-checked={item.enabled}
                      aria-label={`${item.displayName} enabled`}
                      disabled={update.isPending}
                      onClick={() => void toggleEnabled(item)}
                      data-testid={`mcp-toggle-${item.slug}`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canManage && editing && (editing.id == null || current) && (
        <div
          className="dialog-overlay"
          data-testid="mcp-editor"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div
            className="dialog"
            role="dialog"
            aria-modal="true"
            aria-label={
              editing.id == null
                ? "Add business integration"
                : `Manage ${current?.displayName}`
            }
          >
            <div className="dialog-head">
              <span className="set-card-icon">
                <Icon name="briefcase" size={16} />
              </span>
              <h3>
                {editing.id == null
                  ? "Add integration"
                  : `Manage ${current?.displayName}`}
              </h3>
              <button
                type="button"
                className="icon-btn"
                onClick={close}
                aria-label="Close"
              >
                <Icon name="close" size={16} />
              </button>
            </div>

            <div className="dialog-body">
              {validationErrors.length > 0 && (
                <div
                  role="alert"
                  className="field-error"
                  data-testid="mcp-validation-errors"
                >
                  <strong>Check these fields:</strong>
                  <ul>
                    {validationErrors.map((message, index) => (
                      <li key={index}>{message}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* The ordinary path asks for only what cannot be inferred. */}
              <h4 className="set-section-title">Required details</h4>
              <div className="set-form-grid">
                <label className="field">
                  <span>Name</span>
                  <input
                    value={form.displayName}
                    onChange={(event) =>
                      setForm({ ...form, displayName: event.target.value })
                    }
                    placeholder="Monday"
                    data-testid="mcp-display-name"
                  />
                </label>
                <label className="field">
                  <span>{form.protocol === "text-webhook" ? "Webhook URL" : "MCP endpoint"}</span>
                  <input
                    value={form.endpoint}
                    onChange={(event) =>
                      setForm({ ...form, endpoint: event.target.value })
                    }
                    placeholder={form.protocol === "text-webhook" ? "https://example.com/search" : "https://mcp.monday.com/mcp"}
                    data-testid="mcp-endpoint"
                  />
                </label>
              </div>
              <p className="field-hint" data-testid="mcp-auto-detect-hint">
                {form.protocol === "mcp" && form.authKind === "oauth2-user"
                  ? "OAuth endpoints, PKCE and client registration are detected from the MCP server automatically when you save. Open the section below only to review or override them."
                  : "The defaults are ready to use. Open the section below only to change the integration type, authentication or other manual settings."}
              </p>

              <details className="set-advanced" data-testid="mcp-advanced">
                <summary>Advanced and detected settings</summary>
              {editing.id == null && (
                <label className="field">
                  <span>Integration type</span>
                  <select
                    value={form.protocol}
                    onChange={(event) => {
                      const protocol = event.target.value as FormState["protocol"];
                      setForm({
                        ...form,
                        protocol,
                        authKind: protocol === "text-webhook" ? "none" : form.authKind,
                        transportKind: protocol === "text-webhook" ? "header" : form.transportKind,
                      });
                    }}
                    data-testid="mcp-protocol"
                  >
                    <option value="mcp">MCP server</option>
                    <option value="text-webhook">Text webhook (exclusive search)</option>
                  </select>
                  {form.protocol === "text-webhook" && (
                    <p className="field-hint">
                      Receives POST JSON with a <code>query</code> field and returns plain text.
                      When a user enables it, only this source is searched; an empty response
                      never falls back to Microsoft 365, the web, or another tool.
                    </p>
                  )}
                </label>
              )}
              <div className="set-form-grid">
                {editing.id == null ? (
                  <label className="field">
                    <span>ID / slug (optional)</span>
                    <input
                      value={form.slug}
                      onChange={(event) => setForm({ ...form, slug: event.target.value })}
                      placeholder={suggestedSlug(form.displayName, form.endpoint)}
                      data-testid="mcp-slug"
                    />
                    <p className="field-hint">
                      Generated from the name when left empty. Override it only
                      when another stable ID is required; it cannot change later.
                    </p>
                  </label>
                ) : (
                  <label className="field">
                    <span>ID / slug (required)</span>
                    <input value={form.slug} readOnly data-testid="mcp-slug-readonly" />
                    <p className="field-hint">
                      Permanent — connections and tool preferences are keyed to it.
                    </p>
                  </label>
                )}
              </div>
              {/* ── Step 2: authentication ──────────────────────────────── */}
              <label className="field">
                <span>Logo (optional)</span>
                <input
                  value={form.icon}
                  onChange={(event) =>
                    setForm({ ...form, icon: event.target.value })
                  }
                  placeholder="https://example.com/logo.png"
                  data-testid="mcp-logo"
                />
                <p className="field-hint">
                  Paste an HTTPS image URL, or use an icon name such as database.
                </p>
                <McpLogo icon={form.icon} />
              </label>

              <h4 className="set-section-title">Authentication</h4>
              <p className="field-hint">
                {form.protocol === "text-webhook"
                  ? "Choose no authentication, or send a shared hash/token in a custom header."
                  : "Choose the method your MCP provider requires. For OAuth, press Detect settings — most providers publish their own endpoints and will issue a client ID, so there is nothing to look up."}
              </p>
              <label className="field">
                <span>Authentication type</span>
                <select
                  value={form.authKind}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      authKind: event.target.value as AuthKind,
                      // Changing the strategy resets the secret field to "being
                      // set": whatever is stored belongs to the previous
                      // strategy and will be cleared by the API anyway.
                      secret: "",
                    })
                  }
                  data-testid="mcp-auth-kind"
                >
                  {(
                    (form.protocol === "text-webhook"
                      ? ["none", "service-account"]
                      : ["oauth2-user", "token-user", "none", "service-account"]) as AuthKind[]
                  ).map((kind) => (
                    <option key={kind} value={kind}>
                      {AUTH_LABELS[kind]}
                    </option>
                  ))}
                </select>
              </label>

              {form.authKind === "service-account" && (
                <p className="field-hint field-error" data-testid="mcp-shared-warning">
                  <strong>Shared credential.</strong> Every user&rsquo;s Copilot
                  question will authenticate as this deployment rather than as
                  themselves, and the business system&rsquo;s own audit log will
                  show one identity for everybody. Prefer OAuth 2.0 or a personal
                  API token for anything user-facing.
                </p>
              )}

              {form.authKind === "token-user" && (
                <div className="set-form-grid">
                  <label className="field">
                    <span>What to call the token</span>
                    <input
                      value={form.credentialLabel}
                      onChange={(event) =>
                        setForm({ ...form, credentialLabel: event.target.value })
                      }
                      placeholder="Hudu API key"
                      data-testid="mcp-credential-label"
                    />
                    <p className="field-hint">
                      Shown to each user on their own Connect form.
                    </p>
                  </label>
                  <label className="field">
                    <span>Hint (optional)</span>
                    <input
                      value={form.credentialHint}
                      onChange={(event) =>
                        setForm({ ...form, credentialHint: event.target.value })
                      }
                      placeholder="Create one under Account → API keys"
                    />
                  </label>
                </div>
              )}

              {form.authKind === "oauth2-user" && (
                <>
                  <div className="field" data-testid="mcp-callback-url">
                    <span>OAuth callback URL</span>
                    <input
                      readOnly
                      value={callback.data?.callbackUrl ?? ""}
                      placeholder={callback.data?.reason ?? "Loading…"}
                    />
                    <p className="field-hint">
                      Register this exact URL with the provider.{" "}
                      {callback.data?.callbackUrl && (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            void navigator.clipboard?.writeText(
                              callback.data!.callbackUrl!,
                            );
                            toast.success("Callback URL copied");
                          }}
                          data-testid="mcp-copy-callback"
                        >
                          Copy
                        </button>
                      )}
                      {callback.data && !callback.data.callbackUrl && (
                        <span className="field-error"> {callback.data.reason}</span>
                      )}
                    </p>
                  </div>

                  <div className="field" data-testid="mcp-oauth-detect">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={detectOauth.isPending || !form.endpoint.trim()}
                      onClick={() => void detect()}
                      data-testid="mcp-detect-oauth"
                    >
                      {detectOauth.isPending ? "Detecting…" : "Detect settings"}
                    </button>
                    <p className="field-hint">
                      Reads the endpoint&rsquo;s own OAuth metadata and fills in
                      the fields below. Most providers publish it, and most will
                      also issue this deployment a client ID when you save &mdash;
                      so the usual answer is that nothing here has to be typed.
                    </p>
                    {detected && (
                      <div className="field-hint" data-testid="mcp-detected">
                        <strong>Detected from {detected.issuer}.</strong>{" "}
                        {/*
                          The hosts, spelled out. Every URL below was chosen by
                          whoever runs the endpoint, and "is this a host this
                          provider would plausibly use?" is a judgement only the
                          person reading it can make.
                        */}
                        Talks to {detected.hosts.join(", ")}.
                        {detected.registrationEndpoint ? (
                          <>
                            {" "}
                            No client ID needed &mdash; one is registered with the
                            provider when you save.
                          </>
                        ) : (
                          <>
                            {" "}
                            This provider does not register clients
                            automatically, so enter a client ID from its
                            developer console.
                          </>
                        )}
                        {detected.scopesSupported.length > 0 && (
                          <>
                            {" "}
                            Scopes it offers: {detected.scopesSupported.join(", ")}.
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="set-form-grid">
                    <label className="field">
                      <span>Authorization URL (required)</span>
                      <input
                        value={form.authorizationUrl}
                        onChange={(event) =>
                          setForm({ ...form, authorizationUrl: event.target.value })
                        }
                        placeholder="https://auth.monday.com/oauth2/authorize"
                        data-testid="mcp-authorization-url"
                      />
                    </label>
                    <label className="field">
                      <span>Token URL (required)</span>
                      <input
                        value={form.tokenUrl}
                        onChange={(event) =>
                          setForm({ ...form, tokenUrl: event.target.value })
                        }
                        placeholder="https://auth.monday.com/oauth2/token"
                        data-testid="mcp-token-url"
                      />
                    </label>
                  </div>

                  <label className="field">
                    <span>
                      Client ID
                      {form.registrationEndpoint
                        ? " (issued automatically)"
                        : " (required)"}
                    </span>
                    <input
                      value={form.clientId}
                      onChange={(event) =>
                        setForm({ ...form, clientId: event.target.value })
                      }
                      placeholder={
                        form.registrationEndpoint
                          ? "Registered with the provider when you save"
                          : ""
                      }
                      data-testid="mcp-client-id"
                    />
                    {form.registrationEndpoint && (
                      <p className="field-hint">
                        Leave empty and this deployment will be issued one by{" "}
                        {hostOf(form.registrationEndpoint)} when you save. Enter
                        one instead to use an OAuth app you created yourself.
                      </p>
                    )}
                  </label>

                  <SecretField
                    label="Client secret"
                    configured={current?.secrets.hasClientSecret ?? false}
                    value={form.secret}
                    onChange={(secret) => setForm({ ...form, secret })}
                    testId="mcp-client-secret"
                    hint="Usually empty: a client registered automatically is a public PKCE client and has no secret. Stored encrypted and never shown again."
                  />

                  <details className="set-advanced" data-testid="mcp-oauth-advanced">
                    <summary>Advanced OAuth settings (optional)</summary>
                    <label className="field">
                      <span>Revocation URL (optional)</span>
                      <input
                        value={form.revocationUrl}
                        onChange={(event) =>
                          setForm({ ...form, revocationUrl: event.target.value })
                        }
                      />
                    </label>
                    <div className="set-form-grid">
                      <label className="field">
                        <span>Scopes (one per line)</span>
                        <textarea
                          rows={3}
                          value={form.scopesText}
                          onChange={(event) =>
                            setForm({ ...form, scopesText: event.target.value })
                          }
                          placeholder={"boards:read\ndocs:read"}
                        />
                      </label>
                      <div className="field">
                        <label>
                          <span>Audience (optional)</span>
                          <input
                            value={form.audience}
                            onChange={(event) =>
                              setForm({ ...form, audience: event.target.value })
                            }
                          />
                        </label>
                        <label className="set-check">
                          <input
                            type="checkbox"
                            checked={form.pkce}
                            onChange={(event) =>
                              setForm({ ...form, pkce: event.target.checked })
                            }
                            data-testid="mcp-pkce"
                          />{" "}
                          Use PKCE (recommended)
                        </label>
                      </div>
                    </div>
                  </details>
                </>
              )}

              {form.authKind === "service-account" && (
                <SecretField
                  label={form.protocol === "text-webhook" ? "Authentication hash / token" : "Shared credential"}
                  configured={current?.secrets.hasServiceAccountCredential ?? false}
                  value={form.secret}
                  onChange={(secret) => setForm({ ...form, secret })}
                  testId="mcp-service-credential"
                  hint={form.protocol === "text-webhook"
                    ? "Stored encrypted and sent only in the custom header named below."
                    : "Stored encrypted. Every user's call authenticates with it."}
                />
              )}

              <details className="set-advanced" data-testid="mcp-manual-advanced">
                <summary>More manual overrides</summary>
                <label className="field">
                  <span>Description</span>
                  <input
                    value={form.description}
                    onChange={(event) =>
                      setForm({ ...form, description: event.target.value })
                    }
                    placeholder="Monday.com boards and work management"
                  />
                </label>
                <label className="field">
                  <span>Timeout (ms)</span>
                  <input
                    type="number"
                    min={1000}
                    max={60000}
                    value={form.timeoutMs}
                    onChange={(event) =>
                      setForm({ ...form, timeoutMs: Number(event.target.value) })
                    }
                  />
                </label>
                {form.authKind !== "none" && (
                  <div className="set-form-grid">
                    <label className="field">
                      <span>Credential transport</span>
                      <select
                        value={form.transportKind}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            transportKind: event.target.value as
                              | "bearer"
                              | "header",
                          })
                        }
                        data-testid="mcp-transport"
                      >
                        <option value="bearer">Authorization: Bearer</option>
                        <option value="header">Custom header</option>
                      </select>
                    </label>
                    {form.transportKind === "header" && (
                      <label className="field">
                        <span>Header name</span>
                        <input
                          value={form.transportHeader}
                          onChange={(event) =>
                            setForm({
                              ...form,
                              transportHeader: event.target.value,
                            })
                          }
                          placeholder="X-Api-Key"
                        />
                      </label>
                    )}
                  </div>
                )}
              </details>
              </details>

              {/* ── Steps 3–5: connect, discover, allow, enable ─────────── */}
              {current && current.protocol !== "text-webhook" && (
                <>
                  <AdminPersonalConnection
                    integration={current}
                    personal={personalIntegration}
                    loading={personalIntegrations.isPending}
                    unavailable={personalIntegrations.isError}
                  />
                  <ToolApproval
                    integration={current}
                    discovery={discovery}
                    testResult={testResult}
                    busy={{
                      testing: test.isPending,
                      discovering: discover.isPending,
                      saving: setAllowed.isPending,
                    }}
                    onTest={() => void runTest(current)}
                    onDiscover={() => void runDiscovery(current)}
                    onSaveAllowed={(tools) => void saveAllowed(current, tools)}
                    onToggleEnabled={() => void toggleEnabled(current)}
                  />
                </>
              )}
              {current && current.protocol === "text-webhook" && (
                <div className="set-actions-block">
                  <p className="field-hint">
                    This integration exposes one user-facing tool: Search this webhook.
                    It is exclusive whenever the user switches it on.
                  </p>
                  <button
                    type="button"
                    className={current.enabled ? "btn btn-sm btn-secondary" : "btn btn-primary"}
                    onClick={() => void toggleEnabled(current)}
                  >
                    {current.enabled ? "Disable webhook" : "Enable webhook"}
                  </button>
                </div>
              )}
              {/* A destructive action gets its own bordered section at the end
                  of the form, not a bare button beside Save. It still routes
                  through the confirm dialog. */}
              {current && (
                <div className="danger-zone">
                  <span className="panel-row-copy">
                    <strong>Remove this integration</strong>
                    <small>
                      Copilot stops seeing {current.displayName}, and every user
                      connection to it is dropped. This cannot be undone.
                    </small>
                  </span>
                  <span className="spacer" />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger-outline"
                    onClick={() => void onDelete(current)}
                    data-testid="mcp-delete"
                  >
                    Delete integration
                  </button>
                </div>
              )}
            </div>

            <div className="dialog-actions">
              <span className="spacer" />
              <button type="button" className="btn btn-sm btn-secondary" onClick={close}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void save()}
                disabled={
                  saving ||
                  !form.displayName.trim() ||
                  !form.endpoint.trim()
                }
                data-testid="mcp-save"
              >
                {saving ? "Saving…" : editing.id == null ? "Create" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>

    {/* The canvas's Business integrations tab carries three sections, not one:
        the catalog, who may switch its tools on, and the one irreversible act
        that touches every user's connections. */}
    <ToolExposureSection canManage={canManage} />
    <RevokeAllSection canManage={canManage} />
    </>
  );
}

/**
 * A write-only secret field.
 *
 * The three states of the API contract, rendered. When something is stored and
 * the administrator has not touched it, there is NO INPUT AT ALL — just
 * "Configured ✓" and a Replace button. That is deliberate: a password input
 * pre-filled with dots implies the browser holds the value, and it does not.
 * Pressing Replace swaps in an empty field, and Cancel puts the state back to
 * "unchanged" rather than to "clear".
 */
function SecretField({
  label,
  configured,
  value,
  onChange,
  testId,
  hint,
}: {
  label: string;
  configured: boolean;
  /** null = unchanged, "" = clear, string = replace. */
  value: string | null;
  onChange: (value: string | null) => void;
  testId: string;
  hint: string;
}): JSX.Element {
  if (configured && value === null) {
    return (
      <div className="field" data-testid={`${testId}-configured`}>
        <span>{label}</span>
        <p className="field-hint">
          <strong>Configured ✓</strong> — stored encrypted and never displayed.{" "}
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => onChange("")}
            data-testid={`${testId}-replace`}
          >
            Replace
          </button>
        </p>
      </div>
    );
  }
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="password"
        autoComplete="new-password"
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      />
      <p className="field-hint">
        {hint}
        {configured && (
          <>
            {" "}
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => onChange(null)}
              data-testid={`${testId}-cancel`}
            >
              Keep existing
            </button>
          </>
        )}
      </p>
    </label>
  );
}

/**
 * Connects the signed-in administrator's own account. This intentionally uses
 * the same self-scoped endpoints as the Copilot picker: the catalog stays
 * global, while the credential is private to this browser session's user.
 */
function AdminPersonalConnection({
  integration,
  personal,
  loading,
  unavailable,
}: {
  integration: McpAdminIntegrationListItem;
  personal: McpIntegrationSummary | null;
  loading: boolean;
  unavailable: boolean;
}): JSX.Element | null {
  const connect = useConnectMcpIntegration();
  const [tokenOpen, setTokenOpen] = useState(false);
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (integration.auth.kind === "none" || integration.auth.kind === "service-account") {
    return null;
  }

  const status = loading
    ? "Checking your connection…"
    : unavailable || !personal
      ? "Your connection status is unavailable"
      : personal.connected
        ? personal.accountDisplayName
          ? `Connected as ${personal.accountDisplayName}`
          : "Connected to your private account"
        : personal.connectionStatus === "expired"
          ? "Your connection expired"
          : personal.connectionStatus === "revoked"
            ? "Your connection was rejected"
            : "Your account is not connected";

  const startOauth = (): void => {
    window.location.assign(
      mcpOauthStartUrl(integration.id, "/settings/copilot"),
    );
  };

  return (
    <section className="set-personal-connection" data-testid="mcp-admin-connection">
      <div className="set-personal-connection-copy">
        <h4 className="set-section-title">Your private admin connection</h4>
        <p className="field-hint">
          {status}. Test and discovery use only your signed-in account; this
          credential is never shared with other users.
        </p>
      </div>

      {!loading && !unavailable && personal && !personal.connected && !tokenOpen && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={
            integration.auth.kind === "oauth2-user"
              ? startOauth
              : () => setTokenOpen(true)
          }
          data-testid="mcp-admin-connect"
        >
          {personal.connectionStatus === "disconnected" ? "Connect" : "Reconnect"}
        </button>
      )}

      {tokenOpen && integration.auth.kind === "token-user" && (
        <form
          className="set-personal-token"
          onSubmit={(event) => {
            event.preventDefault();
            const value = token.trim();
            if (value.length < 8) {
              setError("Enter your personal API token.");
              return;
            }
            setError(null);
            connect.mutate(
              { integrationId: integration.id, token: value },
              {
                onSuccess: () => {
                  setToken("");
                  setTokenOpen(false);
                },
                onError: (failure) => setError(failure.message),
              },
            );
          }}
        >
          <label className="field">
            <span>{integration.auth.credentialLabel}</span>
            <input
              type="password"
              autoComplete="off"
              spellCheck={false}
              maxLength={4_096}
              value={token}
              onChange={(event) => setToken(event.target.value)}
              data-testid="mcp-admin-token"
            />
            {integration.auth.credentialHint && (
              <p className="field-hint">{integration.auth.credentialHint}</p>
            )}
          </label>
          {error && <p className="field-error" role="alert">{error}</p>}
          <div className="set-row-actions">
            <button type="submit" className="btn btn-primary" disabled={connect.isPending}>
              {connect.isPending ? "Connecting…" : "Connect"}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setToken("");
                setError(null);
                setTokenOpen(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

/**
 * The safe-onboarding half: test, discover, approve, enable.
 *
 * Kept as its own component because it is a different KIND of interaction from
 * the form above it — the form edits fields, this one talks to a business
 * system — and because the approval list is the one place in this screen where
 * getting it wrong has a security consequence rather than a cosmetic one.
 */
function ToolApproval({
  integration,
  discovery,
  testResult,
  busy,
  onTest,
  onDiscover,
  onSaveAllowed,
  onToggleEnabled,
}: {
  integration: McpAdminIntegrationListItem;
  discovery: McpDiscoveryResult | null;
  testResult: McpIntegrationTestResult | null;
  busy: { testing: boolean; discovering: boolean; saving: boolean };
  onTest: () => void;
  onDiscover: () => void;
  onSaveAllowed: (tools: string[]) => void;
  onToggleEnabled: () => void;
}): JSX.Element {
  // The ticked set, seeded from the global allowlist and updated as the
  // administrator ticks. Re-seeded whenever a discovery arrives, so it always
  // describes the list currently on screen.
  const [selected, setSelected] = useState<string[]>(integration.allowedTools);
  useEffect(() => {
    setSelected(integration.allowedTools);
  }, [integration.allowedTools, discovery]);

  const toggle = (tool: McpDiscoveredToolView): void => {
    if (!tool.allowable) return;
    setSelected((current) =>
      current.includes(tool.name)
        ? current.filter((name) => name !== tool.name)
        : [...current, tool.name],
    );
  };

  // Every safe tool this discovery offered. A destructive one is never in here,
  // so "Select all" cannot widen what the runtime will execute — it is the
  // ordinary case (approve what this server offers) in one click instead of
  // thirty, and the API re-checks every name against live discovery anyway.
  const allowable = (discovery?.items ?? []).filter((tool) => tool.allowable);
  const allSelected =
    allowable.length > 0 && allowable.every((tool) => selected.includes(tool.name));

  const selectAll = (checked: boolean): void => {
    setSelected((current) =>
      checked
        ? [...new Set([...current, ...allowable.map((tool) => tool.name)])]
        : // Only the ones on screen are cleared. A name this server no longer
          // offers is still approved, and unticking "all" must not remove an
          // approval this discovery could not show.
          current.filter((name) => !allowable.some((tool) => tool.name === name)),
    );
  };

  return (
    <>
      <h4 className="set-section-title">Tools</h4>
      <p className="field-hint">
        Safe read-only tools are discovered and approved automatically when an
        account connects. Use Discover tools to refresh the list manually; hover
        a tool title for its details.
      </p>

      <div className="set-row-actions" style={{ justifyContent: "flex-start" }}>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={onTest}
          disabled={busy.testing}
          data-testid="mcp-test"
        >
          {busy.testing ? "Testing…" : "Test connection"}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={onDiscover}
          disabled={busy.discovering}
          data-testid="mcp-discover"
        >
          {busy.discovering ? "Discovering…" : "Discover tools"}
        </button>
        <button
          type="button"
          className={integration.enabled ? "btn btn-sm btn-secondary" : "btn btn-primary"}
          onClick={onToggleEnabled}
          data-testid="mcp-toggle-enabled"
        >
          {integration.enabled ? "Disable" : "Enable"}
        </button>
      </div>

      {testResult && (
        <p
          className={`field-hint ${testResult.ok ? "" : "field-error"}`}
          role="status"
          data-testid="mcp-test-result"
        >
          {testResult.message} ({testResult.durationMs} ms)
        </p>
      )}

      {discovery && (
        <div data-testid="mcp-discovery">
          {discovery.items.length === 0 ? (
            <p className="field-hint">This server offered no tools.</p>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        disabled={allowable.length === 0}
                        onChange={(event) => selectAll(event.target.checked)}
                        aria-label="Select all read-only tools"
                        title="Select all read-only tools"
                        data-testid="mcp-tool-check-all"
                      />
                    </th>
                    <th>Tool</th>
                    <th>Safety</th>
                  </tr>
                </thead>
                <tbody>
                  {discovery.items.map((tool) => (
                    <tr key={tool.name} data-testid={`mcp-tool-${tool.name}`}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.includes(tool.name)}
                          // Disabled for a destructive tool. A courtesy, not the
                          // control: the API refuses it regardless of what the
                          // browser sends.
                          disabled={!tool.allowable}
                          onChange={() => toggle(tool)}
                          aria-label={`Allow ${tool.name}`}
                          data-testid={`mcp-tool-check-${tool.name}`}
                        />
                      </td>
                      <td>
                        <div
                          title={[tool.description, tool.title ? tool.name : null]
                            .filter(Boolean)
                            .join(" — ") || undefined}
                        >
                          {tool.title ?? tool.name}
                        </div>
                      </td>
                      <td>
                        {tool.allowable ? (
                          <span className="badge badge-success">Read-only</span>
                        ) : (
                          <span
                            className="badge badge-neutral"
                            data-testid={`mcp-tool-blocked-${tool.name}`}
                          >
                            Write/destructive — cannot be allowed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {discovery.staleAllowedTools.length > 0 && (
                <p className="field-hint field-error" data-testid="mcp-stale-tools">
                  Allowed but no longer offered by this server:{" "}
                  {discovery.staleAllowedTools.join(", ")}. These already do
                  nothing; save to remove them.
                </p>
              )}

              <div className="set-row-actions" style={{ justifyContent: "flex-start" }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => onSaveAllowed(selected)}
                  disabled={busy.saving}
                  data-testid="mcp-save-tools"
                >
                  {busy.saving ? "Saving…" : "Save allowed tools"}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => selectAll(true)}
                  disabled={allowable.length === 0 || allSelected}
                  data-testid="mcp-select-all-tools"
                >
                  Select all
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

/** Re-exported for the tests, which assert the labels the wizard offers. */
export const MCP_AUTH_LABELS: Record<AuthKind, string> = AUTH_LABELS;
export type { McpAdminAuthConfig };

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  const letters = parts.map((part) => part[0] ?? "").join("");
  return (letters || name.slice(0, 2)).toUpperCase();
}

function McpLogo({ icon, name }: { icon: string | null; name?: string }): JSX.Element {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [icon]);

  if (icon?.startsWith("https://") && !failed) {
    return (
      <span className="set-mcp-logo-box">
        <img
          src={icon}
          alt=""
          aria-hidden
          className="set-mcp-logo"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      </span>
    );
  }
  // A catalog entry may name a Font Awesome glyph instead of shipping an asset.
  if (icon && /^[a-z0-9 _-]+$/i.test(icon)) {
    return (
      <span className="set-mcp-logo-box">
        <i className={`fas fa-${icon}`} aria-label="MCP logo placeholder" />
      </span>
    );
  }
  // No vendor asset at all — the text-webhook row is the one case.
  return (
    <span className="set-mcp-logo-box set-mcp-initials" aria-label="MCP logo placeholder">
      {name ? initialsOf(name) : "?"}
    </span>
  );
}

/**
 * Which enabled integrations users may switch on in the composer.
 *
 * The canvas offers three deployment-wide postures — per-user opt-in, always
 * on, and off — as one segmented control whose options explain themselves on
 * hover rather than in permanent body copy.
 *
 * Nothing stores this. `CopilotSettings` is per-user and carries no exposure
 * field, and there is no deployment-settings endpoint to put one in, so the
 * control is drawn at the deployment's actual behaviour — per-user opt-in,
 * which is what the composer does today — and cannot be moved off it. Letting
 * it move would be a switch that changes a screen and nothing else.
 */
const EXPOSURES = [
  {
    id: "optin",
    label: "Per-user opt-in",
    hint: "Users choose which tools to enable per chat. Recommended, and what this deployment does today.",
  },
  {
    id: "always",
    label: "Always on",
    hint: "Every connected integration is offered to Copilot on every turn.",
  },
  {
    id: "off",
    label: "Disabled",
    hint: "Copilot answers from Microsoft 365 content only.",
  },
] as const;

function ToolExposureSection({ canManage }: { canManage: boolean }): JSX.Element {
  return (
    <section className="set-section" data-testid="tool-exposure">
      <div className="set-section-head">
        <div>
          <h2>Tool exposure</h2>
          <p>Which enabled integrations users may switch on in the composer.</p>
        </div>
      </div>

      <div
        className="segmented set-exposure"
        role="radiogroup"
        aria-label="Tool exposure"
      >
        {EXPOSURES.map((exposure) => (
          <button
            key={exposure.id}
            type="button"
            role="radio"
            className="segmented-option"
            aria-checked={exposure.id === "optin"}
            disabled
            data-testid={`exposure-${exposure.id}`}
          >
            {exposure.label}
            <span className="set-exposure-hint">{exposure.hint}</span>
          </button>
        ))}
      </div>

      <BackendUnavailable
        testId="exposure-unavailable"
        what="Changing tool exposure"
        detail={
          canManage
            ? "Your role allows you to manage integrations, but tool exposure is a deployment-wide " +
              "setting and this API stores only per-user Copilot settings. The composer's behaviour " +
              "is per-user opt-in, which is what the control shows."
            : "Tool exposure is a deployment-wide setting this API does not store, and changing it " +
              "would also need the copilot.mcp_integrations_manage action."
        }
        endpoints={["GET /api/v1/copilot/mcp/admin/exposure", "PUT /api/v1/copilot/mcp/admin/exposure"]}
      />
    </section>
  );
}

/**
 * Revoke every user's connections to every business system.
 *
 * A per-user OAuth grant or stored token is held server-side, one per user per
 * integration, and there is no endpoint that clears them in bulk — disabling a
 * catalog entry stops it being offered but does not destroy what people
 * authorized. So the row is drawn where the canvas puts it and says what it
 * would do; an administrator who needs this today does it against the store.
 */
function RevokeAllSection({ canManage }: { canManage: boolean }): JSX.Element {
  return (
    <section className="set-section" data-testid="integrations-danger">
      <div className="set-section-head">
        <div>
          <h2>Danger zone</h2>
          <p>Irreversible actions affecting every user of this deployment.</p>
        </div>
      </div>

      <div className="set-danger-row">
        <div>
          <strong>Revoke all user connections</strong>
          <p>Every user will have to re-authorize each business system.</p>
        </div>
        <button
          type="button"
          className="btn btn-danger-outline"
          disabled
          title={
            canManage
              ? "Revoking every user's connections needs an endpoint this API does not serve."
              : "Revoking connections needs the copilot.mcp_integrations_manage action."
          }
          data-testid="revoke-all"
        >
          Revoke all
        </button>
      </div>

      <BackendUnavailable
        testId="revoke-unavailable"
        what="Revoking every user's connections"
        detail={
          "Per-user grants and tokens are held server-side, one per user per integration, and no " +
          "endpoint clears them in bulk. Disabling a catalog entry above stops it being offered, " +
          "but it does not destroy what people have already authorized."
        }
        endpoints={["POST /api/v1/copilot/mcp/admin/connections/revoke-all"]}
      />
    </section>
  );
}
